import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Activity, Loader2, RefreshCw, AlertTriangle, Check, History, Repeat } from 'lucide-react';
import api from '../lib/api';

const SEV_PILL = { critical: 'pc-pill-red', high: 'pc-pill-red', medium: 'pc-pill-amber', low: 'pc-pill-past' };

const Section = ({ id, section, onFix, fixing }) => (
  <div className="pc-card" data-testid={`pc-health-section-${id}`}>
    <div className="pc-card-h"><h2>{section.label} ({section.items.length})</h2></div>
    {section.items.length === 0 ? <p className="pc-muted" style={{ padding: '0 1rem 1rem' }}>Nothing found.</p> : (
      <div className="pc-scroll">
        <table className="pc-table">
          <tbody>
            {section.items.map((i, n) => (
              <tr key={i.key || `${id}-${n}`}>
                <td style={{ width: 90 }}><span className={`pc-pill ${SEV_PILL[i.severity] || ''}`}>{i.severity}</span></td>
                <td>{i.title}<div className="pc-muted pc-mono" style={{ fontSize: 11 }}>{i.detail}</div></td>
                <td style={{ width: 130, textAlign: 'right' }}>
                  {i.key && (
                    <button className="pc-btn" onClick={() => onFix(i)} disabled={fixing === i.key}
                      style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      data-testid={`pc-health-fix-${i.key}`}>
                      {fixing === i.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Mark as fixed
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const CreditBanner = ({ credit }) => credit?.status !== 'low' ? null : (
  <div className="pc-card" role="alert" data-testid="pc-ai-credit-low" style={{ background: '#3a0d0d', borderColor: '#b91c1c', padding: '1rem' }}>
    <strong style={{ color: '#fecaca' }}><AlertTriangle className="pc-inline-icon" /> Platform AI credit looks low</strong>
    <p className="pc-muted" style={{ margin: '6px 0 0' }}>AI calls failed with a credit error on {new Date(credit.detected_at).toLocaleString()}. Top up the Universal Key (Profile → Universal Key → Add Balance).</p>
  </div>
);

const FixHistory = ({ rows }) => (
  <div className="pc-card" data-testid="pc-health-fix-history" style={{ marginBottom: 16 }}>
    <div className="pc-card-h"><h2><History className="pc-inline-icon" /> Fix history ({rows.length})</h2></div>
    {rows.length === 0 ? <p className="pc-muted" style={{ padding: '0 1rem 1rem' }}>No issue has been marked as fixed yet.</p> : (
      <div className="pc-scroll">
        <table className="pc-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r.fix_id} data-testid={`pc-health-fix-history-row-${r.fix_id}`}>
                <td>
                  {r.title || r.item_key}
                  {r.times_fixed > 1 && (
                    <span className="pc-pill pc-pill-amber" style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 3 }}
                      data-testid={`pc-health-repeat-${r.fix_id}`}>
                      <Repeat className="h-3 w-3" /> fixed {r.times_fixed}×
                    </span>
                  )}
                  <div className="pc-muted" style={{ fontSize: 11 }}>
                    by {r.marked_by_name || r.marked_by || 'platform owner'} · hidden until {new Date(r.hidden_until).toLocaleDateString()}
                  </div>
                </td>
                <td style={{ width: 190, textAlign: 'right', whiteSpace: 'nowrap' }} className="pc-muted">
                  {new Date(r.marked_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const PlatformHealth = () => {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [running, setRunning] = useState(false);
  const [fixing, setFixing] = useState('');
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const load = useCallback(() => api.get('/platform/health-reports').then(r => setData(r.data)).catch(() => toast.error('Could not load health reports')), []);
  const loadHistory = useCallback(() => api.get('/platform/health-reports/fix-history').then(r => setHistory(r.data.history)).catch(() => toast.error('Could not load fix history')), []);
  useEffect(() => { load(); }, [load]);
  const toggleHistory = () => {
    setShowHistory((v) => !v);
    if (!history) loadHistory();
  };
  const runNow = async () => {
    setRunning(true);
    try {
      await api.post('/platform/health-reports/run');
      toast.success('Scan started — results appear in about a minute');
      timer.current = setTimeout(() => { setIdx(0); load(); setRunning(false); }, 60000);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Scan failed');
      setRunning(false);
    }
  };
  const markFixed = async (item) => {
    setFixing(item.key);
    try {
      const { data: res } = await api.post(`/platform/health-reports/items/${item.key}/fix`, null, { params: { title: item.title } });
      toast.success(`Marked as fixed — hidden for ${res.days} days`);
      await load();
      if (history) await loadHistory();
    } catch {
      toast.error('Could not mark it as fixed');
    } finally {
      setFixing('');
    }
  };

  if (!data) return <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;  const report = data.reports[idx];
  return (
    <div data-testid="pc-health-page">
      <div className="pc-page-head">
        <div><h1><Activity className="pc-inline-icon" /> App health</h1>
          <p className="pc-sub">Automatic daily scan of errors, code bugs, security leaks and needed improvements. Emailed to the platform owner. Marking an issue as fixed hides it for 7 days.</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="pc-btn" style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={toggleHistory} data-testid="pc-health-fix-history-toggle">
            <History className="h-4 w-4" /> {showHistory ? 'Hide fix history' : 'Fix history'}
          </button>
          <button className="pc-btn pc-btn-primary" style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={runNow} disabled={running} data-testid="pc-health-run">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Scan now
          </button>
        </div>
      </div>
      <CreditBanner credit={data.ai_credit} />
      {showHistory && (history ? <FixHistory rows={history} /> : <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>)}
      {!report ? <p className="pc-empty">No scans yet — the first one runs within the hour.</p> : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <select className="pc-select" value={idx} onChange={(e) => setIdx(Number(e.target.value))} data-testid="pc-health-report-select">
              {data.reports.map((r, n) => <option key={r.report_id} value={n}>{`${new Date(r.created_at).toLocaleString()} · ${r.trigger}`}</option>)}
            </select>
            <span className={`pc-pill ${report.critical ? 'pc-pill-red' : 'pc-pill-approved'}`} data-testid="pc-health-urgent">{report.critical} urgent</span>
          </div>
          {Object.entries(report.sections).map(([id, s]) => <Section key={id} id={id} section={s} onFix={markFixed} fixing={fixing} />)}
        </>
      )}
    </div>
  );
};
